import { useEffect, useState } from 'react';
import './chatBotDataModal.css';
import {
    supBackfillChatBotTripIds,
    supCreateChatBotData,
    supDeleteChatBotData,
    supGetChatBotData,
    supUpdateChatBotData
} from '../../../api/supportAdmin';
import ChatBotDataTable from './ChatBotDataTable';
import ChatBotDataToolbar from './ChatBotDataToolbar';
import ChatBotDataForm from './ChatBotDataForm';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../../components/tripStrap/tripStrapFun/cancelBooking';
import ChatBotDataPagination from './ChatBotDataPagination';
import CloseIcon from '../../../assets/icons/trvto_close.svg';

const ChatBotDataModal = ({ open, onClose }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editData, setEditData] = useState(null);
    const [type, setType] = useState('');
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [deleteItem, setDeleteItem] = useState(null);
    const [backfilling, setBackfilling] = useState(false);

    const fetchChatBotData = async () => {
        try {
            setLoading(true);

            const response = await supGetChatBotData({
                type,
                search,
                page,
                limit
            });

            setData(response.data.data);
            setTotal(response.data.total);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchChatBotData();
        }
        // eslint-disable-next-line
    }, [open, type, search, page]);

    const handleAdd = () => {
        if (showForm && !editData) {
            setShowForm(false);
            return;
        }

        setEditData(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditData(item);
        setShowForm(true);
    };

    const handleDelete = async (item) => {
        try {
            const response = await supDeleteChatBotData(deleteItem._id);

            toast.success(response.data.message);

            if (editData?._id === deleteItem._id) {
                setEditData(null);
                setShowForm(false);
            }

            setDeleteItem(null);

            fetchChatBotData();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to delete.');
        } finally {
            setDeleteItem(null);
        }
    };

    // Build multipart form data. The WhatsApp image FILE goes in "image"
    // (the backend uploads it to Meta); other files are never sent.
    const buildPayload = (formData) => {
        const payload = new FormData();

        Object.entries(formData).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (['instagramImage', 'whatsAppImage', 'bannerImageFile'].includes(key)) return;
            payload.append(key, value);
        });

        if (formData.whatsAppImage instanceof File) {
            payload.append('image', formData.whatsAppImage);
        }

        return payload;
    };

    const handleSubmit = async (formData) => {
        try {
            setSaving(true);

            const payload = buildPayload(formData);

            const response = editData
                ? await supUpdateChatBotData(editData._id, payload)
                : await supCreateChatBotData(payload);

            toast.success(
                response?.data?.message || (editData ? 'Chatbot data updated successfully.' : 'Chatbot data added successfully.')
            );

            await fetchChatBotData();

            setShowForm(false);
            setEditData(null);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Something went wrong.');
        } finally {
            setSaving(false);
        }
    };

    const handleBackfill = async () => {
        try {
            setBackfilling(true);

            const response = await supBackfillChatBotTripIds();

            toast.success(response?.data?.message || 'Trip IDs backfilled successfully.');

            await fetchChatBotData();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to backfill trip IDs.');
        } finally {
            setBackfilling(false);
        }
    };

    if (!open) return null;
    return (
        <div className='cbd-overlay'>
            <div className='cbd-modal'>
                <div className='cbd-header'>
                    <div>
                        <h2>Manage Chatbot Data</h2>
                        <p>Manage Cities, Destinations and Trip Types used by the chatbot.</p>
                    </div>

                    <img src={CloseIcon} alt='closeIcon' width={30} height={30} onClick={onClose} />
                </div>
                <div className='cbd-container'>
                    <div className={`cbd-table-section`}>
                        <ChatBotDataToolbar
                            type={type}
                            setType={setType}
                            search={search}
                            setSearch={setSearch}
                            onAdd={handleAdd}
                            showForm={showForm}
                            editData={editData}
                            onBackfill={handleBackfill}
                            backfilling={backfilling}
                        />

                        <ChatBotDataTable
                            data={data}
                            loading={loading}
                            onEdit={handleEdit}
                            setDeleteItem={setDeleteItem}
                        />
                        <ChatBotDataPagination page={page} total={total} limit={limit} onPageChange={setPage} />
                    </div>

                    <div className={`cbd-form-section ${showForm ? 'open' : ''}`}>
                        {showForm && (
                            <ChatBotDataForm
                                editData={editData}
                                type={type}
                                loading={saving}
                                onSubmit={handleSubmit}
                                onClose={() => {
                                    setShowForm(false);
                                    setEditData(null);
                                }}
                            />
                        )}
                    </div>
                </div>
                {deleteItem && (
                    <ConfirmationModal
                        setShowModal={setDeleteItem}
                        handleConfirm={() => handleDelete(deleteItem)}
                        message={'Do you want to delete?'}
                    />
                )}
            </div>
        </div>
    );
};

export default ChatBotDataModal;
